import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ToastContainer, toast } from "react-toastify";
import { FaEye, FaEyeSlash, FaLock } from "react-icons/fa";
import FullPageLoader from "../components/FullPageLoader";
import api from "../services/api";
import "react-toastify/dist/ReactToastify.css";

const S = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@400;500;600&display=swap');
.auth-page {
  min-height: 100vh; background: #f9f7f4;
  display: flex; align-items: center; justify-content: center;
  font-family: 'DM Sans', sans-serif; padding: 24px 16px;
  position: relative; overflow: hidden;
}
.auth-blob { position: absolute; border-radius: 50%; pointer-events: none; z-index: 0; }
.auth-blob-1 { width: 420px; height: 420px; background: radial-gradient(circle, rgba(26,92,56,0.08) 0%, transparent 70%); top: -100px; right: -100px; }
.auth-blob-2 { width: 300px; height: 300px; background: radial-gradient(circle, rgba(26,92,56,0.06) 0%, transparent 70%); bottom: -60px; left: -60px; }
.auth-card {
  background: #fff; border-radius: 24px; border: 1px solid #e8e4df;
  box-shadow: 0 8px 40px rgba(0,0,0,0.08); padding: 44px 40px;
  width: 100%; max-width: 420px; position: relative; z-index: 1;
}
.auth-logo { display: flex; justify-content: center; margin-bottom: 8px; }
.auth-logo img { height: 42px; object-fit: contain; }
.auth-title { font-family: 'Playfair Display', serif; font-size: 1.75rem; font-weight: 700; color: #1a1a1a; text-align: center; margin-bottom: 6px; }
.auth-subtitle { font-size: 0.88rem; color: #8a8a8a; text-align: center; margin-bottom: 28px; line-height: 1.5; }
.af-label { display: block; font-size: 0.78rem; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase; color: #1a5c38; margin-bottom: 6px; }
.af-input-wrap { position: relative; margin-bottom: 16px; }
.af-input {
  width: 100%; border: 1.5px solid #e8e4df; border-radius: 11px;
  padding: 11px 44px 11px 42px; font-family: 'DM Sans', sans-serif;
  font-size: 0.92rem; color: #1a1a1a; background: #fff; outline: none;
  transition: border-color 0.18s; box-sizing: border-box;
}
.af-input:focus { border-color: #1a5c38; }
.af-input::placeholder { color: #bbb; }
.af-input:disabled { background: #fafaf9; color: #aaa; }
.af-input-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #aaa; font-size: 0.9rem; pointer-events: none; }
.af-eye { position: absolute; right: 14px; top: 50%; transform: translateY(-50%); cursor: pointer; color: #aaa; font-size: 0.95rem; background: none; border: none; padding: 0; display: flex; align-items: center; transition: color 0.15s; }
.af-eye:hover { color: #1a5c38; }
.af-hint { font-size: 0.78rem; color: #aaa; margin-top: -8px; margin-bottom: 16px; }
.af-error { background: #fef2f2; border: 1px solid #fcd5d5; border-radius: 9px; padding: 10px 14px; font-size: 0.84rem; color: #d94f3d; margin-bottom: 14px; }
.af-invalid-token {
  background: #fef2f2; border: 1px solid #fcd5d5; border-radius: 12px;
  padding: 16px; text-align: center; margin-bottom: 20px;
}
.af-invalid-token p { font-size: 0.88rem; color: #d94f3d; margin: 0 0 10px; }
.af-invalid-token a { color: #1a5c38; font-weight: 600; text-decoration: none; font-size: 0.88rem; }
.af-submit {
  width: 100%; background: #1a5c38; color: #fff; border: none; border-radius: 12px; padding: 14px;
  font-family: 'DM Sans', sans-serif; font-size: 0.96rem; font-weight: 700; cursor: pointer;
  transition: background 0.18s, transform 0.14s; display: flex; align-items: center; justify-content: center; gap: 8px;
}
.af-submit:hover:not(:disabled) { background: #134429; transform: translateY(-1px); }
.af-submit:disabled { background: #a0c4b0; cursor: not-allowed; }
.af-spinner { width: 18px; height: 18px; border: 2px solid rgba(255,255,255,0.4); border-top-color: #fff; border-radius: 50%; animation: afspin 0.7s linear infinite; }
@keyframes afspin { to { transform: rotate(360deg); } }
.af-back { text-align: center; font-size: 0.88rem; color: #8a8a8a; margin-top: 20px; }
.af-back a { color: #1a5c38; font-weight: 600; text-decoration: none; }
.af-back a:hover { opacity: 0.75; }
.af-links { display: flex; flex-direction: column; align-items: center; gap: 10px; margin-top: 20px; }
.af-links a { font-size: 0.88rem; color: #1a5c38; font-weight: 600; text-decoration: none; }
.af-links a:hover { opacity: 0.75; }
.af-links span { font-size: 0.88rem; color: #8a8a8a; }
@media (max-width: 480px) { .auth-card { padding: 32px 20px; border-radius: 18px; } .auth-title { font-size: 1.5rem; } }
`;

const ResetPassword = () => {
  const [password,     setPassword]     = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error,        setError]        = useState("");
  const [loading,      setLoading]      = useState(false);
  const [tokenValid,   setTokenValid]   = useState(true);

  const navigate     = useNavigate();
  const { token }    = useParams();
  const { t }        = useTranslation();

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      setError(t("auth.invalidToken"));
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token || !tokenValid) {
      toast.error(t("auth.invalidToken"));
      return;
    }
    if (password.length < 6) {
      const msg = t("auth.passwordTooShort");
      setError(msg); toast.error(msg); return;
    }
    setLoading(true);
    try {
      await api.post(`/users/reset-password/${token}`, { password });
      toast.success(t("auth.passwordUpdated"));
      setTimeout(() => navigate("/login", { replace: true }), 2000);
    } catch (err) {
      const msg = err.response?.data?.message || t("auth.resetFailed");
      setError(msg);
      toast.error(msg);
      if (err.response?.status === 400 || err.response?.status === 404) setTokenValid(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{S}</style>
      {loading && <FullPageLoader text={t("auth.updatingPassword")} />}
      <div className="auth-page">
        <div className="auth-blob auth-blob-1" />
        <div className="auth-blob auth-blob-2" />
        <div className="auth-card">
          <div className="auth-logo"><img src="/images/Logo1.png" alt="FMart" /></div>
          <h1 className="auth-title">{t("auth.resetTitle")}</h1>
          <p className="auth-subtitle">{t("auth.resetSubtitle")}</p>

          {/* Invalid token state */}
          {(!token || !tokenValid) && (
            <div className="af-invalid-token">
              <p>{t("auth.linkExpired")}</p>
              <Link to="/forgot-password">{t("auth.requestNewLink")} →</Link>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <span className="af-label">{t("auth.newPassword")}</span>
            <div className="af-input-wrap">
              <FaLock className="af-input-icon" />
              <input
                className="af-input"
                type={showPassword ? "text" : "password"}
                placeholder={t("auth.min6chars")}
                value={password}
                onChange={e => setPassword(e.target.value)}
                disabled={loading || !tokenValid}
                required minLength={6}
              />
              <button type="button" className="af-eye"
                onClick={() => setShowPassword(v => !v)}
                disabled={!tokenValid}>
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            <p className="af-hint">{t("auth.min6charsHint")}</p>

            {error && tokenValid && <div className="af-error">{error}</div>}

            <button type="submit" className="af-submit" disabled={loading || !tokenValid}>
              {loading ? <><div className="af-spinner" /> {t("auth.updating")}</> : t("auth.updatePassword")}
            </button>
          </form>

          <div className="af-links">
            <Link to="/forgot-password">{t("auth.requestNewLink")}</Link>
            <span>—</span>
            <Link to="/login">{t("auth.backToLogin")}</Link>
          </div>
        </div>
        <ToastContainer position="top-right" autoClose={3000} />
      </div>
    </>
  );
};

export default ResetPassword;